import { currentViewer } from "@/platform/session/current-viewer"

import type { QueryClient } from "@tanstack/react-query"

export class SupersededViewerError extends Error {
  constructor(cause?: unknown) {
    super("The viewer changed while this request was in flight", cause === undefined ? undefined : { cause })
    this.name = "SupersededViewerError"
  }
}

let generation = 0
const listeners = new Set<() => void>()

export const subscribeViewerBoundary = (listener: () => void): (() => void) => {
  listeners.add(listener)
  return () => {
    listeners.delete(listener)
  }
}

export const crossViewerBoundary = (): void => {
  generation += 1
  listeners.forEach((listener) => listener())
}

export const viewerBoundaryGeneration = (): number => generation

export const holdViewerBoundary = (): (() => boolean) => {
  const held = generation
  return () => held === generation
}

export interface ViewerScope {
  readonly viewer: string
  readonly isCurrent: () => boolean
}

export const scopeToViewer = (queryClient: QueryClient, viewer: string): ViewerScope | null => {
  if (currentViewer(queryClient) !== viewer) return null
  const held = holdViewerBoundary()
  return { viewer, isCurrent: () => held() && currentViewer(queryClient) === viewer }
}

type Placeholder<T> = (previousData: T | undefined, previousQuery: { meta?: Record<string, unknown> | undefined } | undefined) => T | undefined

export const onlyForViewer =
  <T>(viewer: string | null, placeholder: Placeholder<T>): Placeholder<T> =>
  (previousData, previousQuery) =>
    previousQuery?.meta?.viewer === (viewer ?? undefined) ? placeholder(previousData, previousQuery) : undefined
